<template>
  <Modal
    v-if="showAuthenticationDialog"
    :enableFooter="false"
    @close="complete(false)"
  >
    <template v-slot:header>
      <div></div>
    </template>
    <template v-slot:body>
      <div>
        <SignUpForm
          v-if="page == signUpPage"
          @signUp="signUp"
          @signInHereButton="signInHereButton"
        />
        <LoginForm
          v-if="page == loginPage"
          :signingIn="signingIn"
          :showForgetPasswordLink="false"
          @login="login"
          @createAnAccountButton="createAnAccountButton"
        />
        <ConfirmAccountForm
          v-if="page == confirmAccountPage"
          v-model="confirmAccountModel"
          @confirmAccount="confirmAccount"
          @createAnAccountButton="createAnAccountButton"
        />
        <SnackbarNotifier
          :isShow="isShowSnackBar"
          :message="responseMessage"
          :snackType="snackbarType"
          :colour="snackBarColour"
          @eReset="resetSnackBar"
        />
      </div>
    </template>
  </Modal>
</template>

<script>
import SignUpForm from "@/components/SignUpForm.vue";
import LoginForm from "@/components/LoginForm.vue";
import ConfirmAccountForm from "@/components/ConfirmAccountForm.vue";
import { ApiService } from "@/services/ApiService";
import { error } from "@/services/LoggerService";
import { login } from "@/mixins/shared";
import mixins from "vue-typed-mixins";
import SnackbarNotifier from "@/components/SnackbarNotifier.vue";
import SnackbarNotifierMixin from "@/mixins/SnackbarNotifierMixins";
import { AuthActionTypes } from "@/store/auth/actions";
import Modal from "@/components/Modal.vue";

export const AuthenticationDialog = mixins(SnackbarNotifierMixin).extend({
  components: {
    SignUpForm,
    LoginForm,
    ConfirmAccountForm,
    SnackbarNotifier,
    Modal,
  },
  props: {
    showAuthenticationDialog: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      signUpPage: "signup",
      loginPage: "login",
      confirmAccountPage: "confirmaccount",
      page: "signup",
      signingIn: "off",
      confirmAccountModel: {
        email: "",
      },
    };
  },
  methods: {
    async signUp(customerInfo) {
      try {
        const result = await ApiService.Instance.signUp(customerInfo);

        if (result) {
          this.showNotifier({
            message: `Account created successfully. Please check your email for the confirmation link`,
            snackbarType: "success",
          });
          setTimeout(this.setPage(this.loginPage), 3500);
        }
      } catch (e) {
        error(e);

        if (e.response.data.errorType == "EmailAlreadyExist") {
          this.showNotifier({
            message: `An account with this email already exists`,
            snackbarType: "error",
          });
        } else {
          this.showNotifier({
            message: `Failed to create account`,
            snackbarType: "error",
          });
        }
      }
    },
    async login({ email, password }) {
      try {
        this.signingIn = "on";
        const result = await login(email, password);
        if (!result) {
          this.signingIn = "off";
          return;
        }

        if (result.challengeName == "NEW_PASSWORD_REQUIRED") {
          this.$nextTick(() => {
            this.confirmAccountModel.email = email;
            this.setPage(this.confirmAccountPage);
          });
          this.signingIn = "off";
          return;
        }
        this.$nextTick(() => {
          this.complete(true);
        });

        this.signingIn = "off";
      } catch (e) {
        error(e);
        this.signingIn = "off";
        this.handleError(e);
      }
    },
    handleError(e) {
      if (
        e.message == "User does not exist." ||
        e.message == "Incorrect username or password."
      ) {
        this.showNotifier({
          message: `Email or Password incorrect`,
          snackbarType: "error",
        });

        return;
      }

      if (e.message == "User is not confirmed.") {
        this.showNotifier({
          message: `Please check your email for the confirmation link`,
          snackbarType: "error",
        });

        return;
      }

      this.showNotifier({
        message: `Unsucessful signin`,
        snackbarType: "error",
      });
    },
    async confirmAccount({ email, password }) {
      try {
        const newPasswordResult = await this.completeNewPassword(password);
        if (!newPasswordResult) {
          return;
        }
        const loginResult = await login(email, password);
        if (!loginResult) {
          return;
        }
        await this.confirmUserAccount();
        this.complete(true);
      } catch (e) {
        alert(e);
        error(e);
      }
    },
    async confirmUserAccount() {
      try {
        await ApiService.Instance.confirmAccount();
      } catch (e) {
        error(e);
      }
    },
    async completeNewPassword(newPassword) {
      return this.$store.dispatch(
        AuthActionTypes.CompleteNewPassword,
        newPassword
      );
    },
    signInHereButton() {
      this.setPage(this.loginPage);
    },
    createAnAccountButton() {
      this.setPage(this.signUpPage);
    },
    setPage(page) {
      this.page = page;
    },
    complete(success) {
      success = success ?? true;
      this.$emit("dismiss", success);
    },
  },
});

export const AuthenticationMixin = {
  components: {
    AuthenticationDialog,
  },
  data() {
    return {
      authenticationDialogData: {
        showAuthenticationDialog: false,
        resolve: () => {},
        reject: () => {},
      },
    };
  },
  computed: {
    isLoggedIn() {
      return this.$store.getters.isAuthenticated;
    },
  },
  methods: {
    async authenticateUser() {
      return new Promise((resolve, reject) => {
        try {
          if (this.isLoggedIn) {
            resolve(true);
            return;
          }
          this.authenticationDialogData.resolve = resolve;
          this.authenticationDialogData.reject = reject;
          this.authenticationDialogData.showAuthenticationDialog = true;
        } catch (e) {
          error(e);
          this.rejectAuthenticationDialogCallback(
            "Failed to authenticate the user"
          );
          throw e;
        }
      });
    },
    handleAuthenticationDialogResponse(response) {
      try {
        response = response ?? false;
        this.resolveAuthenticationDialogCallback(response);
      } catch (e) {
        error(e);
        this.rejectAuthenticationDialogCallback(
          "Failed to authenticate the user"
        );
      }
    },
    resolveAuthenticationDialogCallback(response) {
      const resolve = this.authenticationDialogData.resolve;
      this.dismissAuthenticationDialog();

      if (!resolve) {
        return;
      }
      resolve(response);
    },
    rejectAuthenticationDialogCallback(response) {
      const reject = this.authenticationDialogData.reject;
      this.dismissAuthenticationDialog();

      if (!reject) {
        return;
      }
      reject(response);
    },
    dismissAuthenticationDialog() {
      this.authenticationDialogData.showAuthenticationDialog = false;
      this.authenticationDialogData.resolve = () => {};
      this.authenticationDialogData.reject = () => {};
    },
  },
};

// <AuthenticationDialog
//     v-bind="authenticationDialogData"
//     @dismiss="handleAuthenticationDialogResponse"
// />

export default AuthenticationDialog;
</script>

<style lang="scss" scoped>
</style>